import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { PresetOut } from '@/api/types';
import { Icon, isLayout, sectionType, Segmented, Thumb, useToast } from '@/design-system';
import { patchCachedPreset, setFavorite } from '@/features/library/api';
import { usePresetLabels } from '@/features/library/labels';
import { errorText } from '@/shell/errorText';
import { galleryQuery, pinOf, type GalleryQuery, type PinnedPreset } from './api';
import { useStructureLabels } from './labels';
import { activate } from './PageStack';
import s from './list.module.css';

export type GalleryStyle = 'all' | 'light' | 'soft' | 'inv' | 'acc' | 'fav';
const STYLES: GalleryStyle[] = ['all', 'light', 'soft', 'inv', 'acc', 'fav'];
/** Header, footer and promotions are wide: their cards use the wide minimums (design: galMin, czGalMin). */
export const WIDE_TYPES = new Set(['header', 'footer', 'promotions']);

/** The filters the PM sets (kept while moving between sections; the layout resets per section). */
export type GalleryFilters = { search: string; style: GalleryStyle; layout: string; big: boolean };
export const DEFAULT_FILTERS: GalleryFilters = { search: '', style: 'all', layout: 'all', big: true };

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** The server query a set of filters means. */
export function queryOf(type: string, f: GalleryFilters, search: string): GalleryQuery {
  return {
    section_type: type,
    layout: f.layout === 'all' ? undefined : f.layout,
    color_mode: f.style === 'all' || f.style === 'fav' ? undefined : f.style,
    favorites: f.style === 'fav' || undefined,
    search: search.trim() || undefined,
  };
}

const isFiltered = (f: GalleryFilters) => f.style !== 'all' || f.layout !== 'all' || !!f.search.trim();

/** The library's presets of one section type: the cards, the type's total and the layout facets. */
export function useGallery(type: string, f: GalleryFilters) {
  const search = useDebounced(f.search, 250);
  const q = useInfiniteQuery(galleryQuery(queryOf(type, f, search)));
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const first = q.data?.pages[0];
  return { q, items, total: first?.facets?.total ?? first?.total ?? 0, shown: first?.total ?? 0, layouts: first?.facets?.layouts ?? [] };
}

export type GalleryData = ReturnType<typeof useGallery>;

/** «36 дизайнов · показано 36». */
export function useCountLine(g: GalleryData): string {
  const { t } = useTranslation('structure');
  return t('gallery.count', { designs: t('gallery.designs', { count: g.total }), shown: g.shown });
}

/** Search and the style filter (Все / Светлые / Мягкие / Тёмные / Акцентные / ★). */
export function GalleryFilterRow({ filters: f, onFilters, short, className }: { filters: GalleryFilters; onFilters: (f: GalleryFilters) => void; short?: boolean; className?: string }) {
  const { t } = useTranslation('structure');
  return (
    <div className={className ?? s.galFilters}>
      <label className={s.search} data-short={short || undefined}>
        <Icon name="search" size={14} />
        <input type="search" aria-label={t('gallery.search')} placeholder={short ? t('gallery.search_short') : t('gallery.search')} value={f.search} onChange={(e) => onFilters({ ...f, search: e.target.value })} />
      </label>
      <Segmented
        variant="compact"
        className={s.styleSeg}
        label={t('gallery.style_label')}
        value={f.style}
        options={STYLES.map((k) => ({ value: k, label: t(`gallery.styles.${k}`) }))}
        onChange={(style) => onFilters({ ...f, style })}
      />
    </div>
  );
}

type GridProps = {
  type: string;
  data: GalleryData;
  filters: GalleryFilters;
  /** the section's pinned designs (C4: each carries «✓ Выбран»); undefined = nothing chosen yet (adding) */
  pinned?: PinnedPreset[];
  /** the cards' minimum width (CSS) */
  min: string;
  /** a preset (or null = «AI решит сам») was clicked */
  onPick: (pin: PinnedPreset | null, preset: PresetOut | null) => void;
  /** hover = try-on (null when the pointer leaves) */
  onHover?: (pin: PinnedPreset | null) => void;
};

/**
 * The cards: «AI решит сам» first (only without a filter), then each preset at the section's gallery
 * ratio with «✓ Выбран», «Черновик» (an Admin's drafts), the name, «‹mode› · vN» and the favourite star.
 */
export function GalleryGrid({ type, data, filters: f, pinned, min, onPick, onHover }: GridProps) {
  const { t } = useTranslation('structure');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const { modeName } = useStructureLabels();
  const { name: presetName } = usePresetLabels();
  const def = sectionType(type);
  const { q, items } = data;
  const sentinel = useRef<HTMLDivElement>(null);
  const filtered = isFiltered(f);
  const chosen = new Set((pinned ?? []).map((p) => p.preset_id));
  const aiChosen = !!pinned && pinned.length === 0;

  // The next page loads as the PM nears the end of the list.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  const toggleFav = (p: PresetOut) => {
    const on = !p.is_favorite;
    patchCachedPreset(qc, p.id, { is_favorite: on });
    setFavorite(p.id, on).catch((e: unknown) => {
      patchCachedPreset(qc, p.id, { is_favorite: !on });
      toast(errorText(e, tc));
    });
  };
  const pick = (p: PresetOut) => {
    const pin = pinOf(p);
    if (!pin) return toast(t('toast.no_version'));
    onPick(pin, p);
  };

  return (
    <>
      <div className={s.grid} style={{ '--card-min': min } as CSSProperties} role="list" aria-label={t('gallery.label')}>
        {!filtered && (
          <div role="listitem">
            <div
              role="button"
              tabIndex={0}
              className={s.card}
              data-ai
              aria-pressed={aiChosen}
              onClick={() => onPick(null, null)}
              onKeyDown={(e) => activate(e, () => onPick(null, null))}
              onMouseEnter={() => onHover?.(null)}
            >
              <div className={s.art} style={{ aspectRatio: def.galleryRatio }}>
                <span className={s.artAi}>
                  <Icon name="sparkles" size={18} />
                  {t('gallery.ai_pick')}
                </span>
                {aiChosen && <Chosen />}
              </div>
              <div className={s.cardFoot}>
                <span className={s.cardText}>
                  <span className={s.cardName}>{t('gallery.ai_name')}</span>
                  <span className={s.cardSub}>{t('gallery.ai_sub')}</span>
                </span>
              </div>
            </div>
          </div>
        )}
        {items.map((p) => {
          const on = chosen.has(p.id);
          const name = presetName(p);
          const pin = pinOf(p);
          return (
            <div key={p.id} role="listitem">
              <div
                role="button"
                tabIndex={0}
                className={s.card}
                aria-pressed={on}
                aria-label={`${name} · ${modeName(p.color_mode)}`}
                data-preset={p.id}
                onClick={() => pick(p)}
                onKeyDown={(e) => activate(e, () => pick(p))}
                onMouseEnter={() => pin && onHover?.(pin)}
                onMouseLeave={() => onHover?.(null)}
              >
                <div className={s.art} style={{ aspectRatio: def.galleryRatio }}>
                  {/* C6 (PLAN D21): a real catalog preset shows its rendered screenshot; the others their wireframe. */}
                  {p.thumbnail_url ? (
                    <img className={s.shot} src={p.thumbnail_url} alt="" loading="lazy" />
                  ) : (
                    <Thumb layout={p.layout} mode={p.color_mode} mirrored={p.mirrored} className={s.fill} style={{ position: 'absolute' }} />
                  )}
                  {on && <Chosen />}
                  {p.status !== 'published' && <span className={s.draft}>{t('gallery.draft')}</span>}
                </div>
                <div className={s.cardFoot}>
                  <span className={s.cardText}>
                    <span className={s.cardName}>{name}</span>
                    <span className={s.cardSub}>{t('gallery.sub', { mode: modeName(p.color_mode), version: p.version })}</span>
                  </span>
                  <button
                    type="button"
                    className={s.star}
                    title={t('gallery.fav')}
                    aria-label={`${t('gallery.fav')}: ${name}`}
                    aria-pressed={p.is_favorite}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFav(p);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Icon name="star" size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {!q.isPending && items.length === 0 && filtered && <div className={s.empty}>{t('gallery.empty')}</div>}
      <div ref={sentinel} className={s.sentinel} />
    </>
  );
}

function Chosen() {
  const { t } = useTranslation('structure');
  return (
    <span className={s.chosen}>
      <Icon name="check" size={11} />
      {t('gallery.chosen')}
    </span>
  );
}

type Props = {
  sectionType: string;
  pinned: PinnedPreset[];
  filters: GalleryFilters;
  onFilters: (f: GalleryFilters) => void;
  onPick: GridProps['onPick'];
  onHover?: GridProps['onHover'];
};

/** List mode's gallery panel: «Дизайн для «‹section›»», the count, Крупно / Компактно, the filters, the layout chips with counts, the cards. */
export function Gallery({ sectionType: type, pinned, filters: f, onFilters, onPick, onHover }: Props) {
  const { t } = useTranslation('structure');
  const { t: tl } = useTranslation('library');
  const { section } = useStructureLabels();
  const def = sectionType(type);
  const data = useGallery(type, f);
  const count = useCountLine(data);
  const wide = WIDE_TYPES.has(type);
  const min = f.big ? (wide ? '420px' : '300px') : wide ? '300px' : '210px';
  return (
    <section className={s.gallery} aria-label={t('gallery.title', { section: section(type) })}>
      <div className={s.galHead}>
        <div className={s.galTitleRow}>
          <Icon name={def.icon} size={17} className={s.galIcon} />
          <h2 className={s.galTitle}>{t('gallery.title', { section: section(type) })}</h2>
          <span className={s.galCount}>{count}</span>
          <Segmented
            variant="compact"
            className={s.sizeSeg}
            label={t('gallery.size_label')}
            value={f.big ? 'big' : 'small'}
            options={[
              { value: 'big', label: t('gallery.big') },
              { value: 'small', label: t('gallery.small') },
            ]}
            onChange={(v) => onFilters({ ...f, big: v === 'big' })}
          />
        </div>
        <GalleryFilterRow filters={f} onFilters={onFilters} />
        <div className={s.chips} role="radiogroup" aria-label={t('gallery.layouts_label')}>
          {[{ layout: 'all', count: data.total }, ...data.layouts].map((l) => (
            <button key={l.layout} type="button" role="radio" aria-checked={f.layout === l.layout} className={s.chip} onClick={() => onFilters({ ...f, layout: l.layout })}>
              {l.layout === 'all' ? t('gallery.all_layouts') : isLayout(l.layout) ? tl(`layouts.${l.layout}`) : l.layout}
              <span className={s.chipCount}>{l.count}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={s.galBody}>
        <GalleryGrid type={type} data={data} filters={f} pinned={pinned} min={min} onPick={onPick} onHover={onHover} />
      </div>
    </section>
  );
}
